import { HALCY_WAVE_PATH } from '@marketing-optimizer/themes';
import type { JSX } from 'react';

/**
 * Props for the HalcyLogo component
 */
interface HalcyLogoProps {
  /** Additional CSS classes for the container */
  className?: string;
  /** CSS classes for the mark SVG */
  iconClassName?: string;
  /** CSS classes for the "Halcy" text */
  textClassName?: string;
  /** Whether to show the "Halcy" text alongside the mark */
  showText?: boolean;
}

/**
 * Reusable Halcy brand mark.
 *
 * The approved ring and tapered wave share their geometry with dashboard
 * and social assets. Both inherit `currentColor` from the surrounding ink.
 *
 * @example
 * // Full logo with text
 * <HalcyLogo iconClassName="h-6 w-6 text-blue-500" />
 *
 * @example
 * // Mark only
 * <HalcyLogo showText={false} iconClassName="h-8 w-8 text-blue-500" />
 */
export function HalcyLogo({
  className = '',
  iconClassName = 'h-6 w-6 sm:h-7 sm:w-7',
  textClassName = 'text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl',
  showText = true,
}: HalcyLogoProps): JSX.Element {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <svg
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        className={iconClassName}
      >
        <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" strokeWidth="4" />
        <path
          fill="currentColor"
          d={HALCY_WAVE_PATH}
        />
      </svg>
      {showText && <span className={textClassName}>halcy</span>}
    </div>
  );
}
